import { createFileRoute } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useMotionValue } from 'motion/react'
import { BODY_FONT, HEADING_FONT, horizontalScrim } from '@/lib/constants'
import { COLORS, GUTTER, NEUTRAL8_RGB } from '@/components/home/tokens'
import { useIsMobile } from '@/lib/hooks'
import { HubSpotLegacyForm } from '@/components/HubSpotForm'
import { type CymaticShape } from '@/components/CymaticInteractive'

const CymaticInteractive = lazy(() => import('@/components/CymaticInteractive'))

// Random Chladni mode pair picked on load — same framework as the company hero.
const SHAPE_POOL: [number, number][] = [[2, 1], [3, 1], [3, 2], [4, 1], [2, 2]]
function randomShape(): CymaticShape {
  const shuffled = [...SHAPE_POOL].sort(() => Math.random() - 0.5)
  return { modes: [shuffled[0], shuffled[1]] }
}

// HubSpot form configuration — the demo-request form. Built in HubSpot's legacy
// editor, so it uses the hbspt.forms.create embed (portal/region default to the
// shared values in HubSpotForm).
const HUBSPOT_FORM_ID = 'e3254b6e-968a-4fe5-a24d-9e216ccd2df4'

export const Route = createFileRoute('/contact')({
  component: ContactPage,
})

function ContactPage() {
  const isMobile = useIsMobile()

  // Cymatic backdrop — same framework as the company hero: a random Chladni
  // figure forms softly on load; static (not scroll-driven), reduced-motion aware.
  const [cymaticShape] = useState(randomShape)
  const cymaticScroll = useMotionValue(1)
  const [reducedMotion, setReducedMotion] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    // eslint-disable-next-line react-hooks/set-state-in-effect -- subscribing to a media query. Seeding from `mq.matches` is the initial read of the same external system the listener below subscribes to; splitting them would leave the first paint wrong.
    setReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return (
    <>
      {/* Cymatic backdrop — the same particle scene as the top of the company
          page. Fixed full-viewport behind the page content (the form sits
          above it); a vertical gradient settles the particles into the page bg
          toward the bottom. */}
      {!reducedMotion && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
          <Suspense fallback={null}>
            <CymaticInteractive
              scrollProgress={cymaticScroll}
              shape={cymaticShape}
              resolveAnimation={{ from: 0, to: 0.5, durationMs: 1400 }}
              particleColor="#564F44"
            />
          </Suspense>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(180deg, rgba(7,54,28,0) 0%, rgba(${NEUTRAL8_RGB},0.5) 68%, rgba(${NEUTRAL8_RGB},1) 100%)`,
            }}
          />
          {/* Horizontal scrim — solid left fading to transparent right; anchors
              the left-aligned heading over the cymatic. */}
          <div
            aria-hidden
            style={{ position: 'absolute', inset: 0, background: horizontalScrim(NEUTRAL8_RGB) }}
          />
        </div>
      )}
      <div
        style={{
          minHeight: '100vh',
          padding: isMobile ? `15vh 24px 100px` : `0 ${GUTTER}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '40px' : 'clamp(48px, 8vw, 120px)',
            width: '100%',
            maxWidth: 1100,
            margin: '0 auto',
          }}
        >
          {/* Heading + supporting line — left column (platform hero styles) */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                fontFamily: HEADING_FONT,
                fontWeight: 200,
                fontSize: 'var(--text-4xl)',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                color: COLORS.neutral2,
                margin: 0,
              }}
            >
              See it on your own work.
            </h1>
            <p
              style={{
                fontFamily: BODY_FONT,
                fontSize: 'var(--text-base)',
                lineHeight: 1.55,
                color: COLORS.neutral4,
                maxWidth: '44ch',
                margin: '32px 0 0',
              }}
            >
              A working demo of intent-aware security: real telemetry, real interventions, real timelines. Tell us your hardest use case and we&apos;ll show you how Ent handles it.
            </p>
          </div>

          {/* HubSpot Embedded Form — right column. Loads regardless of cookie
              consent: the demo-request form is treated as functional. */}
          {/* minHeight reserves space for the async-injected form so the footer
              below doesn't shift down when it renders (CLS). Tuned to the
              rendered demo-form height; validate against the live form. */}
          <div style={{ width: isMobile ? '100%' : '492px', maxWidth: '492px', flexShrink: 0, minHeight: isMobile ? 640 : 560 }}>
            <HubSpotLegacyForm formId={HUBSPOT_FORM_ID} targetId="contact-hubspot-form" />
          </div>
        </div>
      </div>
    </>
  )
}
